import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import { openConsentSettings } from '../lib/consent';
import { HOURS, hoursText } from '../config/business';

const Footer = () => {
  return (
    <footer className="bg-[#222222] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4" data-testid="footer-logo">
              <img src="/logo.png" alt="SeknuTo.cz – zahradnické služby" width="40" height="40" className="w-10 h-10" />
              <span className="font-bold text-xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
                SeknuTo<span className="text-[#3FA34D]">.cz</span>
              </span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-4">
              Profesionální zahradnické služby pro váš dokonalý trávník a zahradu. Rychle, spolehlivě a transparentně.
            </p>
            <div className="text-gray-400 text-sm leading-relaxed mb-4 not-italic" data-testid="footer-business-info">
              <p className="text-white font-medium">Dušan Macháček</p>
              <p className="text-gray-300">Fibichova 1294, 544 01 Dvůr Králové nad Labem</p>
              <p className="text-gray-300">IČO: 24889229</p>
              <p className="text-gray-300">
                Tel:{' '}
                <a href="tel:+420730588372" className="hover:text-[#3FA34D] transition-colors">
                  730 588 372
                </a>
              </p>
              <p className="text-gray-300">
                E-mail:{' '}
                <a href="mailto:info@seknuto.cz" className="hover:text-[#3FA34D] transition-colors">
                  info@seknuto.cz
                </a>
              </p>
            </div>
            <p className="text-[#3FA34D] font-medium text-sm">
              🌱 Trávník bez starostí!
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h2 className="font-semibold text-lg mb-4 text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Rychlé odkazy
            </h2>
            <ul className="space-y-3">
              {[
                { href: '/sluzby', label: 'Služby' },
                { href: '/cenik', label: 'Jak to funguje' },
                { href: '/rezervace', label: 'Rezervace' },
                { href: '/o-nas', label: 'O nás' },
                { href: '/kontakt', label: 'Kontakt' },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-[#3FA34D] transition-colors text-sm"
                    data-testid={`footer-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h2 className="font-semibold text-lg mb-4 text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Kontakt
            </h2>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:+420730588372"
                  className="flex items-center gap-3 text-gray-400 hover:text-[#3FA34D] transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 text-[#3FA34D]" />
                  +420 730 588 372
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/420730588372"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-gray-400 hover:text-[#25D366] transition-colors text-sm"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 text-[#25D366]" />
                  WhatsApp
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@seknuto.cz"
                  className="flex items-center gap-3 text-gray-400 hover:text-[#3FA34D] transition-colors text-sm"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 text-[#3FA34D]" />
                  info@seknuto.cz
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin className="w-4 h-4 text-[#3FA34D] mt-0.5" />
                <span>Dvůr Králové nad Labem<br />a okolí do 50 km</span>
              </li>
            </ul>
          </div>

          {/* Working Hours */}
          <div>
            <h2 className="font-semibold text-lg mb-4 text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Pracovní doba
            </h2>
            <ul className="space-y-2 text-sm text-gray-400">
              {HOURS.map((h) => (
                <li key={h.label} className="flex justify-between">
                  <span>{h.label}</span>
                  <span className={h.opens ? 'text-white' : 'text-gray-400'}>{hoursText(h)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 p-4 bg-[#3FA34D]/10 rounded-lg">
              <p className="text-[#3FA34D] text-sm font-medium">
                💬 Rychlá odpověď přes WhatsApp!
              </p>
            </div>
          </div>

          {/* Oblast působení */}
          <div>
            <h2 className="font-semibold text-lg mb-4 text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Služby a oblast působení
            </h2>
            <ul className="space-y-2 text-sm">
              {[
                { href: '/pravidelne-sekani-travy', label: 'Sekání trávy' },
                { href: '/sekani-prerostle-travy', label: 'Sekání přerostlé trávy' },
                { href: '/strihani-keru-kaceni-stromu', label: 'Stříhání keřů a tújí' },
                { href: '/kaceni-stromu', label: 'Kácení stromů' },
                { href: '/likvidace-pozemku', label: 'Likvidace zarostlých pozemků' },
                { href: '/cisteni-zahrady', label: 'Čištění a úklid zahrady' },
                { href: '/vertikutace-travniku', label: 'Vertikutace trávníku' },
                { href: '/realizace-zahrad', label: 'Realizace zahrad' },
                { href: '/pokladani-travniku', label: 'Pokládání trávníku' },
                { href: '/udrzba-zahrady', label: 'Údržba zahrady' },
                { href: '/odvoz-bioodpadu', label: 'Odvoz bioodpadu' },
                { href: '/hrabani-listi', label: 'Hrabání listí' },
                { href: '/rez-ovocnych-stromu', label: 'Řez ovocných stromů' },
                { href: '/uklid-snehu', label: 'Úklid sněhu' },
                { href: '/udrzba-zelene-pro-firmy', label: 'Pro firmy a SVJ' },
                { href: '/zahradnik-dvur-kralove', label: 'Zahradník Dvůr Králové' },
                { href: '/sekani-travy-hradec-kralove', label: 'Sekání trávy Hradec Králové' },
                { href: '/sekani-travy-trutnov',  label: 'Sekání trávy Trutnov' },
                { href: '/sekani-travy-vrchlabi', label: 'Sekání trávy Vrchlabí' },
                { href: '/sekani-travy-jaromer',  label: 'Sekání trávy Jaroměř' },
                { href: '/sekani-travy-nachod',   label: 'Sekání trávy Náchod' },
                { href: '/sekani-travy-hostinne', label: 'Sekání trávy Hostinné' },
                { href: '/sekani-travy-horice',   label: 'Sekání trávy Hořice' },
                { href: '/sekani-travy-jicin',    label: 'Sekání trávy Jičín' },
                { href: '/sekani-travy-ceska-skalice', label: 'Sekání trávy Česká Skalice' },
                { href: '/sekani-travy-nova-paka', label: 'Sekání trávy Nová Paka' },
                { href: '/sekani-travy-upice', label: 'Sekání trávy Úpice' },
                { href: '/sekani-travy-hronov', label: 'Sekání trávy Hronov' },
                { href: '/sekani-travy-smirice', label: 'Sekání trávy Smiřice' },
              ].map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-gray-400 hover:text-[#3FA34D] transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} SeknuTo.cz. Všechna práva vyhrazena.
          </p>
          <p className="text-gray-400 text-xs text-center">
            Sekání trávy, stříhání tújí, kácení stromů a úklid zahrad · Dvůr Králové nad Labem · Hradec Králové · Trutnov · Vrchlabí · Jaroměř · Náchod · Hostinné · Hořice · Jičín · Česká Skalice · Nová Paka · Úpice · Hronov · Smiřice
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/gdpr" className="text-gray-400 hover:text-white transition-colors">
              Ochrana osobních údajů
            </Link>
            <Link to="/obchodni-podminky" className="text-gray-400 hover:text-white transition-colors">
              Obchodní podmínky
            </Link>
            <button
              type="button"
              onClick={openConsentSettings}
              className="text-gray-400 hover:text-white transition-colors"
              data-testid="footer-cookie-settings"
            >
              Nastavení cookies
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
